import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bygningsdelAPI, bildeAPI } from '../services/api';
import BildeOpplaster from '../components/BildeOpplaster';
import BildeGalleri from '../components/BildeGalleri';
import VidereUndersokelser from '../components/VidereUndersokelser';
import { lagTekVeiledning } from '../utils/tek-krav';
import './Befaring.css'; // Gjenbruker Befaring.css for styling

function BygningsdelBefaring() {
  const { id } = useParams(); // bygningsdel ID
  const navigate = useNavigate();
  const [bygningsdel, setBygningsdel] = useState(null);
  const [sporsmal, setSporsmal] = useState([]);
  const [svar, setSvar] = useState({});
  const [ikkeBesvartBegrunnelse, setIkkeBesvartBegrunnelse] = useState({});
  const [tgiuBegrunnelse, setTgiuBegrunnelse] = useState({}); // Årsak for TGIU-valg
  const [kommentarer, setKommentarer] = useState({}); // Kommentarer for alle TG-spørsmål
  const [aarsakKonsekvens, setAarsakKonsekvens] = useState({}); // §2-22: Årsak og konsekvens for TG2/TG3
  const [kostnadsanslag, setKostnadsanslag] = useState({}); // §2-22: Kostnadsanslag for TG3
  const [kostnadsestimat, setKostnadsestimat] = useState({
    kategori: '',
    eksakt: '',
    beskrivelse: '',
    usikkerhet: 'middels',
    anbefaling: ''
  }); // Detaljert kostnadsestimat for bygningsdel
  const [typeKommentar, setTypeKommentar] = useState(''); // Type-spesifikasjon (f.eks. "Taktekking: Teglstein")
  const [renoveringsAar, setRenoveringsAar] = useState(''); // Renoveringsår for TEK-vurdering
  const [aiValidering, setAiValidering] = useState({}); // AI-validering av årsak/konsekvens
  const [aiValiderer, setAiValiderer] = useState({}); // Loading state for AI-validering
  const [bilder, setBilder] = useState([]);
  const [sporsmalBilder, setSporsmalBilder] = useState({}); // Spørsmål-spesifikke bilder
  const [videreUndersokelser, setVidereUndersokelser] = useState([]); // Videre undersøkelser
  const [regelForslag, setRegelForslag] = useState([]); // Regelbaserte forslag
  const [tgiuAiForslag, setTgiuAiForslag] = useState({}); // AI-forslag for TGIU-begrunnelser per spørsmål
  const [tgiuAiLoading, setTgiuAiLoading] = useState({}); // Loading state for AI-forslag
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBilde, setUploadingBilde] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Beregn TEK-veiledning basert på byggeår og renoveringsår
  const tekVeiledning = useMemo(() => {
    if (!bygningsdel) return null;
    const byggeaar = bygningsdel.byggeaar; // Antar byggeår kommer fra bygningsdel-objektet
    const renoveringsaar = renoveringsAar ? parseInt(renoveringsAar) : null;
    return lagTekVeiledning(bygningsdel.bygningsdel_type, byggeaar, renoveringsaar);
  }, [bygningsdel, renoveringsAar]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const response = await bygningsdelAPI.getSporsmal(id);
      setBygningsdel(response.data.bygningsdel);
      setSporsmal(response.data.sporsmal);

      // Last type_kommentar hvis den finnes
      if (response.data.bygningsdel.type_kommentar) {
        setTypeKommentar(response.data.bygningsdel.type_kommentar);
      }

      // Last renoverings_aar hvis den finnes
      if (response.data.bygningsdel.renoverings_aar) {
        setRenoveringsAar(response.data.bygningsdel.renoverings_aar);
      }

      // Last bilder
      const bilderResponse = await bygningsdelAPI.getBilder(id);
      setBilder(bilderResponse.data);

      // Last videre undersøkelser
      await fetchVidereUndersokelser();

      // Last eksisterende svar hvis de finnes
      // TODO: implementer lasting av eksisterende svar
    } catch (err) {
      setError('Kunne ikke hente befaring data: ' + (err.response?.data?.error || err.message));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVidereUndersokelser = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/api/eiendom/${bygningsdel?.eiendom_id}/videre-undersokelser`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filtrer kun undersøkelser for denne bygningsdelen
        const bygningsdelUndersokelser = data.filter(u => u.bygningsdel_id === parseInt(id));
        setVidereUndersokelser(bygningsdelUndersokelser);
      }
    } catch (error) {
      console.error('Feil ved henting av videre undersøkelser:', error);
    }
  };

  const evaluerRegelForSvar = async (sporsmalId, sporsmalTekst, svarVerdi) => {
    // Sjekk om svar inneholder TGIU eller andre trigger-verdier
    if (!svarVerdi || !bygningsdel) return;

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/api/evaluer-regler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          bygningsdel_type: bygningsdel.bygningsdel_type,
          sporsmal_tekst: sporsmalTekst,
          svar: svarVerdi,
          svar_verdi: null,
          eiendom_id: bygningsdel.eiendom_id,
          bygningsdel_id: parseInt(id),
          sporsmal_id: sporsmalId
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Oppdater regelforslag (regler som ikke er auto-opprett)
        if (data.matchende_regler && data.matchende_regler.length > 0) {
          setRegelForslag(data.matchende_regler);
        }

        // Refresh videre undersøkelser hvis noen ble auto-opprettet
        if (data.antall_auto_opprettet > 0) {
          await fetchVidereUndersokelser();
          setSuccess(`${data.antall_auto_opprettet} videre undersøkelse(r) opprettet automatisk basert på NS3600-krav`);
          setTimeout(() => setSuccess(''), 5000);
        }
      }
    } catch (error) {
      console.error('Feil ved evaluering av regler:', error);
    }
  };

  const handleSvarChange = (sporsmalId, sporsmalTekst, value) => {
    setSvar({ ...svar, [sporsmalId]: value });
    // Evaluer regler når svar endres
    evaluerRegelForSvar(sporsmalId, sporsmalTekst, value);
    // Hent AI-forslag hvis TGIU er valgt
    if (value && value.includes('TGIU')) {
      fetchTgiuAiForslag(sporsmalId, sporsmalTekst);
    }
  };

  // Hent AI-forslag for TGIU-begrunnelser
  const fetchTgiuAiForslag = async (sporsmalId, sporsmalTekst) => {
    if (!bygningsdel) return;

    setTgiuAiLoading({ ...tgiuAiLoading, [sporsmalId]: true });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/api/tgiu-begrunnelser/forslag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bygningsdel_type: bygningsdel.bygningsdel_type,
          sporsmal_tekst: sporsmalTekst
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTgiuAiForslag({ ...tgiuAiForslag, [sporsmalId]: data.forslag || [] });
      }
    } catch (error) {
      console.error('Feil ved henting av TGIU AI-forslag:', error);
    } finally {
      setTgiuAiLoading({ ...tgiuAiLoading, [sporsmalId]: false });
    }
  };

  // Registrer bruk av TGIU-begrunnelse (for ML-ranking)
  const registrerTgiuBruk = async (begrunnelseId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      await fetch(`${API_URL}/api/tgiu-begrunnelser/${begrunnelseId}/bruk`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Feil ved registrering av TGIU-bruk:', error);
    }
  };

  const validateAarsakKonsekvens = async (sporsmalId, beskrivelse, sporsmal) => {
    if (!beskrivelse || beskrivelse.trim().length < 50) {
      return; // Ikke valider hvis for kort
    }

    setAiValiderer({ ...aiValiderer, [sporsmalId]: true });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagnesen.workers.dev';
      const response = await fetch(`${API_URL}/api/ai/validate-aarsak-konsekvens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          beskrivelse: beskrivelse,
          bygningsdelType: bygningsdel?.bygningsdel_type,
          tilstandsgrad: svar[sporsmalId]
        })
      });

      const result = await response.json();
      setAiValidering({ ...aiValidering, [sporsmalId]: result });
    } catch (error) {
      console.error('AI-valideringsfeil:', error);
      // Stille feil - ikke vis feilmelding til bruker
    } finally {
      setAiValiderer({ ...aiValiderer, [sporsmalId]: false });
    }
  };

  const fetchSporsmalBilder = async (sporsmalId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/api/bygningsdel/id/${id}/bilder`, { headers });
      if (response.ok) {
        const alleBilder = await response.json();
        const filtrerte = alleBilder.filter(b => b.sporsmal_id === sporsmalId);
        setSporsmalBilder(prev => ({ ...prev, [sporsmalId]: filtrerte }));
      }
    } catch (error) {
      console.error('Feil ved henting av spørsmålsbilder:', error);
    }
  };

  const handleBildeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validering
    if (!file.type.startsWith('image/')) {
      alert('Kun bildefiler er tillatt');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert('Bildet er for stort. Maksimal størrelse er 10MB');
      return;
    }

    try {
      setUploadingBilde(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('beskrivelse', '');

      await bygningsdelAPI.uploadBilde(id, formData);

      // Last bilder på nytt
      const bilderResponse = await bygningsdelAPI.getBilder(id);
      setBilder(bilderResponse.data);

      setSuccess('✓ Bilde lastet opp!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert('Kunne ikke laste opp bilde: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingBilde(false);
    }
  };

  const handleSlettBilde = async (bildeId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette bildet?')) {
      return;
    }

    try {
      await bildeAPI.delete(bildeId);
      setBilder(bilder.filter(b => b.id !== bildeId));
      setSuccess('✓ Bilde slettet');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert('Kunne ikke slette bilde: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Valider obligatoriske spørsmål
      const valideringsFeil = [];
      const obligatoriskeSporsmal = sporsmal.filter(s => s.obligatorisk);
      const manglerSvar = [];
      const manglerTgiuBegrunnelse = [];

      for (const s of obligatoriskeSporsmal) {
        const harSvar = svar[s.id] && svar[s.id].trim() !== '';
        const harBegrunnelse = ikkeBesvartBegrunnelse[s.id] &&
                              ikkeBesvartBegrunnelse[s.id].trim().length >= 10;

        if (!harSvar && !harBegrunnelse) {
          manglerSvar.push(`• ${s.sporsmal_tekst} - Må enten besvares eller ha begrunnelse`);
        }
      }

      // Valider TGIU-begrunnelser (for alle spørsmål, ikke bare obligatoriske)
      const manglerAarsakKonsekvens = [];
      const manglerKostnadsanslag = [];

      for (const s of sporsmal) {
        if (svar[s.id] && svar[s.id].includes('TGIU')) {
          const harTgiuBegrunnelse = tgiuBegrunnelse[s.id] &&
                                    tgiuBegrunnelse[s.id].trim().length >= 10;
          if (!harTgiuBegrunnelse) {
            manglerTgiuBegrunnelse.push(`• ${s.sporsmal_tekst} - TGIU krever begrunnelse (minimum 10 tegn)`);
          }
        }

        // §2-22: Valider årsak og konsekvens for TG2/TG3
        if (svar[s.id] && (svar[s.id].includes('TG2') || svar[s.id].includes('TG3'))) {
          const harAarsakKonsekvens = aarsakKonsekvens[s.id] &&
                                      aarsakKonsekvens[s.id].trim().length >= 50;
          if (!harAarsakKonsekvens) {
            manglerAarsakKonsekvens.push(`• ${s.sporsmal_tekst} - §2-22 krever beskrivelse av årsak og konsekvens (minimum 50 tegn)`);
          }
        }

        // §2-22: Valider kostnadsanslag for TG3
        if (svar[s.id] && svar[s.id].includes('TG3')) {
          const harKostnadsanslag = kostnadsanslag[s.id] && kostnadsanslag[s.id].kategori;
          if (!harKostnadsanslag) {
            manglerKostnadsanslag.push(`• ${s.sporsmal_tekst} - §2-22 krever kostnadsanslag for TG3`);
          }
        }
      }

      if (manglerSvar.length > 0) {
        valideringsFeil.push({
          kategori: 'Obligatoriske spørsmål (merket med *)',
          feil: manglerSvar,
        });
      }

      if (manglerTgiuBegrunnelse.length > 0) {
        valideringsFeil.push({
          kategori: 'TGIU-begrunnelser (påkrevd etter NS3600:2018)',
          feil: manglerTgiuBegrunnelse,
        });
      }

      if (manglerAarsakKonsekvens.length > 0) {
        valideringsFeil.push({
          kategori: '§2-22: Årsak og konsekvens (påkrevd for TG2/TG3)',
          feil: manglerAarsakKonsekvens,
        });
      }

      if (manglerKostnadsanslag.length > 0) {
        valideringsFeil.push({
          kategori: '§2-22: Kostnadsanslag (påkrevd for TG3)',
          feil: manglerKostnadsanslag,
        });
      }

      // Lagre valideringsfeil for senere visning, men tillat alltid lagring

      // Bygg svar-array med TGIU-begrunnelser, kommentarer, årsak/konsekvens og kostnadsanslag
      const svarArray = Object.entries(svar).map(([sporsmalId, verdi]) => {
        let fullSvar = verdi;

        // Hvis TGIU er valgt, legg til begrunnelsen
        if (verdi.includes('TGIU') && tgiuBegrunnelse[sporsmalId]) {
          fullSvar += ` - Årsak: ${tgiuBegrunnelse[sporsmalId]}`;
        }

        // Hvis TG-valg har kommentar, legg til
        if (verdi.startsWith('TG') && kommentarer[sporsmalId]) {
          fullSvar += ` | Kommentar: ${kommentarer[sporsmalId]}`;
        }

        // Hvis TG2/TG3, legg til årsak og konsekvens (§2-22)
        if ((verdi.includes('TG2') || verdi.includes('TG3')) && aarsakKonsekvens[sporsmalId]) {
          fullSvar += ` | §2-22 Årsak og konsekvens: ${aarsakKonsekvens[sporsmalId]}`;
        }

        // Hvis TG3, legg til kostnadsanslag (§2-22)
        if (verdi.includes('TG3') && kostnadsanslag[sporsmalId]) {
          const ka = kostnadsanslag[sporsmalId];
          let kostnadsText = '';

          if (ka.kategori === 'eksakt' && ka.eksakt) {
            kostnadsText = `${parseInt(ka.eksakt).toLocaleString('nb-NO')} NOK (eksakt)`;
          } else if (ka.kategori === 'under_10k') {
            kostnadsText = 'Under 10 000 kr';
          } else if (ka.kategori === '10k_50k') {
            kostnadsText = '10 000 – 50 000 kr';
          } else if (ka.kategori === '50k_100k') {
            kostnadsText = '50 000 – 100 000 kr';
          } else if (ka.kategori === '100k_300k') {
            kostnadsText = '100 000 – 300 000 kr';
          } else if (ka.kategori === 'over_300k') {
            kostnadsText = 'Over 300 000 kr';
          }

          fullSvar += ` | §2-22 Kostnadsanslag: ${kostnadsText}`;

          if (ka.beskrivelse) {
            fullSvar += ` - Omfatter: ${ka.beskrivelse}`;
          }
          if (ka.usikkerhet) {
            const usikkerhetText = ka.usikkerhet === 'lav' ? 'Lav usikkerhet' :
                                  ka.usikkerhet === 'middels' ? 'Middels usikkerhet' :
                                  'Høy usikkerhet';
            fullSvar += ` (${usikkerhetText})`;
          }
          if (ka.anbefaling) {
            fullSvar += ` - Anbefaling: ${ka.anbefaling}`;
          }
        }

        return {
          sporsmal_id: parseInt(sporsmalId),
          svar_verdi: fullSvar,
        };
      });

      const begrunnelserArray = Object.entries(ikkeBesvartBegrunnelse)
        .filter(([_, begrunnelse]) => begrunnelse && begrunnelse.trim() !== '')
        .map(([sporsmalId, begrunnelse]) => ({
          sporsmal_id: parseInt(sporsmalId),
          svar_verdi: `[IKKE BESVART] Begrunnelse: ${begrunnelse}`,
        }));

      await bygningsdelAPI.saveSporsmalSvar(id, {
        svar: [...svarArray, ...begrunnelserArray]
      });

      // Oppdater status basert på om det er mangler eller ikke
      const nyStatus = valideringsFeil.length === 0 ? 'fullfort' : 'pabegynt';
      await bygningsdelAPI.updateStatus(id, {
        status: nyStatus,
        type_kommentar: typeKommentar,
        renoverings_aar: renoveringsAar ? parseInt(renoveringsAar) : null
      });

      if (valideringsFeil.length === 0) {
        setSuccess('✅ Svar lagret! Bygningsdelen er fullført og klar for rapportgenerering.');
        setError('');
      } else {
        setSuccess('✓ Svar lagret!');

        // Vis tydelig advarsel om mangler
        let mangelmelding = '⚠️ ADVARSEL: Bygningsdelen har mangler som må utbedres før rapport kan genereres.\n\n';
        valideringsFeil.forEach(v => {
          mangelmelding += `${v.kategori}:\n`;
          v.feil.forEach(f => mangelmelding += `  ${f}\n`);
          mangelmelding += '\n';
        });
        mangelmelding += '→ Se kontrollsenteret for fullstendig oversikt over alle mangler i rapporten.';
        setError(mangelmelding);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Kunne ikke lagre svar: ' + (err.response?.data?.error || err.message));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const bygningsdelTypeNavn = (type) => {
    const typer = {
      yttertak: 'Yttertak',
      yttervegger: 'Yttervegger',
      vinduer_dorer: 'Vinduer og dører',
      fundament_sokkel: 'Fundament og sokkel',
      drenering: 'Drenering',
      elektrisk: 'Elektrisk anlegg',
      vvs: 'VVS-anlegg',
      ventilasjon: 'Ventilasjon',
      brannsikkerhet: 'Brannsikkerhet',
    };
    return typer[type] || type;
  };

  const getTypeHjelpetekst = (type) => {
    const hjelpetekster = {
      yttertak: '(f.eks. "Taktekking: Teglstein", "Papp", "Takstein")',
      yttervegger: '(f.eks. "Kledning: Tre panel", "Plast", "Mur")',
      vinduer_dorer: '(f.eks. "Vinduer: Tre/aluminium", "Dører: Stål")',
      fundament_sokkel: '(f.eks. "Fundament: Betong", "Sokkel: Mur")',
      drenering: '(f.eks. "Type: Innvendig drenering", "Opplegg")',
      elektrisk: '(f.eks. "Sikringsskap: IT", "Jordfeilbryter")',
      vvs: '(f.eks. "Rør: Kobber/plast", "Varmeanlegg: Vannbåren")',
      ventilasjon: '(f.eks. "Type: Balansert", "Mekanisk avtrekk")',
      brannsikkerhet: '(f.eks. "Brannvarslere: Optiske", "Slokkeutstyr")',
    };
    return hjelpetekster[type] || '';
  };

  const getTypePlaceholder = (type) => {
    const placeholders = {
      yttertak: 'F.eks: Taktekking av teglstein med underlaget papp',
      yttervegger: 'F.eks: Kledning av trepanel med vindsperre',
      vinduer_dorer: 'F.eks: Vinduer av tre med aluminiumskledning',
      fundament_sokkel: 'F.eks: Fundament av betong, sokkel av mur',
      drenering: 'F.eks: Innvendig drenering med pumpe',
      elektrisk: 'F.eks: IT-system med jordfeilbryter',
      vvs: 'F.eks: Kobborrør, vannbåren varme',
      ventilasjon: 'F.eks: Balansert ventilasjon med varmegjenvinning',
      brannsikkerhet: 'F.eks: Optiske brannvarslere, pulverapparat',
    };
    return placeholders[type] || 'Beskriv materialer, fabrikater eller konstruksjonstype';
  };

  const renderSporsmalInput = (s) => {
    switch (s.svar_type) {
      case 'ja_nei':
        return (
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name={`svar_${s.id}`}
                value="Ja"
                checked={svar[s.id] === 'Ja'}
                onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
              />
              Ja
            </label>
            <label>
              <input
                type="radio"
                name={`svar_${s.id}`}
                value="Nei"
                checked={svar[s.id] === 'Nei'}
                onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
              />
              Nei
            </label>
          </div>
        );

      case 'dropdown':
        let alternativer = [];
        try {
          alternativer = JSON.parse(s.svar_alternativer || '[]');
        } catch (err) {
          console.error('Kunne ikke parse alternativer:', err);
        }

        return (
          <select
            value={svar[s.id] || ''}
            onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
            className="form-select"
          >
            <option value="">-- Velg --</option>
            {alternativer.map((alt, idx) => (
              <option key={idx} value={alt}>
                {alt}
              </option>
            ))}
          </select>
        );

      case 'tall':
        return (
          <input
            type="number"
            value={svar[s.id] || ''}
            onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
            className="form-input"
          />
        );

      case 'tekst':
      default:
        return (
          <textarea
            value={svar[s.id] || ''}
            onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
            className="form-textarea"
            rows={5}
            style={{minHeight: '100px'}}
          />
        );
    }
  };

  if (loading) return <div className="loading">Laster...</div>;
  if (error && !bygningsdel) return <div className="error-message">{error}</div>;
  if (!bygningsdel) return <div className="error-message">Bygningsdel ikke funnet</div>;

  return (
    <div className="befaring-page">
      <nav className="navbar">
        <div className="navbar-content">
          <button onClick={() => navigate(`/eiendom/${bygningsdel.eiendom_id}`)} className="btn-back">
            ← Tilbake til bygningsoversikt
          </button>
          <h1>{bygningsdelTypeNavn(bygningsdel.bygningsdel_type)}</h1>
        </div>
      </nav>

      <div className="befaring-content">
        {error && (
          <div className="error-message" style={{ whiteSpace: 'pre-line', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {success && (
          <div className="success-message" style={{ marginBottom: '20px' }}>
            {success}
          </div>
        )}

        <div className="befaring-header">
          <h2>{bygningsdel.bygningsdel_navn || bygningsdelTypeNavn(bygningsdel.bygningsdel_type)}</h2>
          <p className="befaring-info">
            Befaring basert på §2-18 Forskrift om tryggere bolighandel og NS3600
          </p>

          {/* Type-spesifikasjon felt */}
          <div style={{marginTop: '15px', marginBottom: '15px', padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '5px', border: '1px solid #b3d9ff'}}>
            <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#333'}}>
              📝 Spesifiser type/detaljer {getTypeHjelpetekst(bygningsdel.bygningsdel_type)}
            </label>
            <input
              type="text"
              value={typeKommentar}
              onChange={(e) => setTypeKommentar(e.target.value)}
              placeholder={getTypePlaceholder(bygningsdel.bygningsdel_type)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #b3d9ff',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
            <small style={{color: '#666', fontSize: '12px', marginTop: '5px', display: 'block'}}>
              Valgfritt felt for å beskrive spesifikke materialer, fabrikater eller konstruksjonstype
            </small>
          </div>

          {/* Renoveringsår felt for TEK-vurdering */}
          <div style={{marginTop: '15px', marginBottom: '15px', padding: '12px', backgroundColor: '#fff4e6', borderRadius: '5px', border: '1px solid #ffd699'}}>
            <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#333'}}>
              📅 Renoveringsår (valgfritt)
            </label>
            <input
              type="number"
              value={renoveringsAar}
              onChange={(e) => setRenoveringsAar(e.target.value)}
              placeholder="F.eks. 2011"
              min="1900"
              max={new Date().getFullYear()}
              style={{
                width: '200px',
                padding: '8px',
                border: '1px solid #ffd699',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
            <small style={{color: '#666', fontSize: '12px', marginTop: '5px', display: 'block'}}>
              Hvis bygningsdelen er renovert etter byggeår. Brukes for TEK-vurdering i rapport.
              <br />
              <strong>Eksempel:</strong> Hus fra 1991 → Bad renovert 2011 vil vurderes mot TEK10 (ikke Byggeforskrift 1987)
            </small>
          </div>

          {/* Live TEK-preview */}
          {tekVeiledning && (
            <div style={{
              marginTop: '10px',
              padding: '15px',
              backgroundColor: tekVeiledning.erRenovert ? '#e8f5e9' : '#f5f5f5',
              borderRadius: '8px',
              border: `2px solid ${tekVeiledning.farge}`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: tekVeiledning.farge,
                  color: 'white',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '13px',
                  marginRight: '10px'
                }}>
                  {tekVeiledning.versjon}
                </span>
                <span style={{fontSize: '14px', color: '#333'}}>
                  {tekVeiledning.statusTekst}
                </span>
              </div>

              <div style={{marginTop: '12px'}}>
                <p style={{margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#555'}}>
                  📋 Relevante paragrafer:
                </p>
                <ul style={{margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#666'}}>
                  {tekVeiledning.paragrafer.map((p, idx) => (
                    <li key={idx} style={{marginBottom: '3px'}}>{p}</li>
                  ))}
                </ul>
              </div>

              <div style={{marginTop: '12px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '4px'}}>
                <p style={{margin: '0 0 6px 0', fontSize: '13px', fontWeight: 'bold', color: '#333'}}>
                  ⚙️ Hovedkrav:
                </p>
                <p style={{margin: '0', fontSize: '12px', color: '#555', lineHeight: '1.5'}}>
                  {tekVeiledning.hovedkrav}
                </p>
              </div>

              <div style={{marginTop: '12px'}}>
                <p style={{margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#555'}}>
                  ✅ Testpunkter ved befaring:
                </p>
                <ul style={{margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#666'}}>
                  {tekVeiledning.testpunkter.map((t, idx) => (
                    <li key={idx} style={{marginBottom: '3px'}}>{t}</li>
                  ))}
                </ul>
              </div>

              <div style={{marginTop: '12px', fontSize: '11px', color: '#999', fontStyle: 'italic'}}>
                💡 Denne veiledningen oppdateres automatisk basert på renoveringsår
              </div>
            </div>
          )}

          <p className="befaring-info" style={{ fontSize: '13px', color: '#666' }}>
            Spørsmål merket med <span style={{ color: 'red' }}>*</span> er obligatoriske og må besvares eller begrunnes.
          </p>
        </div>

        {sporsmal.length === 0 ? (
          <div className="empty-state">
            <p>⚠️ Ingen spørsmål funnet for denne bygningsdelen.</p>
            <p>Dette kan være en feil i databasen eller spørsmålsbiblioteket.</p>
          </div>
        ) : (
          <div className="sporsmal-container">
            {sporsmal.map((s, index) => (
              <div key={s.id} className="sporsmal-card" style={{
                marginBottom: '30px',
                padding: '20px',
                backgroundColor: '#ffffff',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
              }}>
                <div className="sporsmal-header" style={{marginBottom: '15px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'}}>
                    <span style={{
                      backgroundColor: '#2196F3',
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      Spørsmål {index + 1}
                    </span>
                    {s.ns3600_referanse && (
                      <span className="ns3600-ref">{s.ns3600_referanse}</span>
                    )}
                  </div>
                  <h3 style={{marginTop: '5px', marginBottom: '0'}}>
                    {s.sporsmal_tekst}
                    {s.obligatorisk === 1 && <span style={{ color: 'red' }}> *</span>}
                  </h3>
                </div>

                {renderSporsmalInput(s)}

                {/* TGIU-begrunnelse hvis valgt */}
                {svar[s.id] && svar[s.id].includes('TGIU') && (
                  <div className="tgiu-container"
                       style={{marginTop: '10px', padding: '15px',
                               backgroundColor: '#e3f2fd', borderLeft: '3px solid #2196F3'}}>
                    <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#1976D2'}}>
                      📋 Årsak til at dette ikke er undersøkt
                    </p>
                    <p style={{margin: '0 0 12px 0', fontSize: '13px', color: '#424242'}}>
                      Etter NS3600:2018 skal det dokumenteres hvorfor en bygningsdel ikke kunne undersøkes:
                    </p>

                    {/* AI-FORSLAG */}
                    {tgiuAiLoading[s.id] && (
                      <p style={{margin: '0 0 10px 0', fontSize: '13px', color: '#1976D2'}}>
                        🤖 Henter AI-forslag basert på spørsmål...
                      </p>
                    )}

                    {tgiuAiForslag[s.id] && tgiuAiForslag[s.id].length > 0 && (
                      <div style={{marginBottom: '12px'}}>
                        <p style={{margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#1976D2'}}>
                          🤖 AI-forslag (klikk for å bruke):
                        </p>
                        {tgiuAiForslag[s.id].slice(0, 3).map((forslag, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setTgiuBegrunnelse({
                                ...tgiuBegrunnelse,
                                [s.id]: forslag.standardtekst
                              });
                              registrerTgiuBruk(forslag.id);
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              marginBottom: '6px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              textAlign: 'left',
                              backgroundColor: idx === 0 ? '#4CAF50' : '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            {idx === 0 ? '⭐' : '💡'} {forslag.standardtekst.substring(0, 80)}...
                            <br />
                            <span style={{fontSize: '11px', opacity: 0.9}}>
                              Score: {forslag.score}/100 • {forslag.kategori}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    <textarea
                      value={tgiuBegrunnelse[s.id] || ''}
                      onChange={(e) => setTgiuBegrunnelse({
                        ...tgiuBegrunnelse, [s.id]: e.target.value
                      })}
                      placeholder="F.eks: Ikke tilgjengelig for inspeksjon, dekket av møbler, krever destruktiv undersøkelse, etc. (minimum 10 tegn)"
                      style={{ width: '100%', minHeight: '80px', padding: '8px',
                               border: '1px solid #2196F3', borderRadius: '4px',
                               fontFamily: 'inherit', fontSize: '14px' }}
                    />
                    {(!tgiuBegrunnelse[s.id] || tgiuBegrunnelse[s.id].length < 10) && (
                      <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#1565C0'}}>
                        Begrunnelse påkrevd (minimum 10 tegn) {tgiuBegrunnelse[s.id] ? `(${tgiuBegrunnelse[s.id].length}/10)` : '(0/10)'}
                      </p>
                    )}
                  </div>
                )}

                {/* Kommentar-felt for ALLE spørsmål (valgfritt) */}
                {svar[s.id] && !svar[s.id].includes('IU') && (
                  <div style={{marginTop: '10px'}}>
                    <label style={{fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '5px', color: '#666'}}>
                      💬 Kommentar (valgfritt)
                    </label>
                    <textarea
                      value={kommentarer[s.id] || ''}
                      onChange={(e) => setKommentarer({...kommentarer, [s.id]: e.target.value})}
                      placeholder="Tilleggskommentar eller presiseringer..."
                      style={{width: '100%', minHeight: '50px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px'}}
                    />
                  </div>
                )}

                {/* §2-22: Årsak og konsekvens for TG2/TG3 (obligatorisk) */}
                {svar[s.id] && (svar[s.id].includes('TG2') || svar[s.id].includes('TG3')) && (
                  <div style={{marginTop: '10px', padding: '15px', backgroundColor: '#fff3cd', borderLeft: '3px solid #ff9800'}}>
                    <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#f57c00'}}>
                      ⚠️ §2-22: Årsak og konsekvens (obligatorisk for TG2/TG3)
                    </p>
                    <p style={{margin: '0 0 8px 0', fontSize: '13px', color: '#424242'}}>
                      Forskriften krever beskrivelse av hva som har forårsaket tilstanden og hvilke konsekvenser dette har:
                    </p>
                    <textarea
                      value={aarsakKonsekvens[s.id] || ''}
                      onChange={(e) => setAarsakKonsekvens({...aarsakKonsekvens, [s.id]: e.target.value})}
                      onBlur={(e) => validateAarsakKonsekvens(s.id, e.target.value, s)}
                      placeholder="Beskriv årsak til tilstanden og konsekvenser for bygningen (minimum 50 tegn)..."
                      style={{width: '100%', minHeight: '80px', padding: '8px', border: '1px solid #ff9800', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px'}}
                    />
                    {(!aarsakKonsekvens[s.id] || aarsakKonsekvens[s.id].length < 50) && (
                      <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#e65100'}}>
                        Beskrivelse påkrevd (minimum 50 tegn) {aarsakKonsekvens[s.id] ? `(${aarsakKonsekvens[s.id].length}/50)` : '(0/50)'}
                      </p>
                    )}

                    {/* AI-validering feedback */}
                    {aiValiderer[s.id] && (
                      <div style={{marginTop: '8px', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px'}}>
                        🤖 AI vurderer beskrivelsen...
                      </div>
                    )}

                    {aiValidering[s.id] && !aiValiderer[s.id] && (
                      <div style={{
                        marginTop: '8px',
                        padding: '10px',
                        backgroundColor: aiValidering[s.id].score >= 7 ? '#e8f5e9' : aiValidering[s.id].score >= 5 ? '#fff3e0' : '#ffebee',
                        borderLeft: `3px solid ${aiValidering[s.id].score >= 7 ? '#4caf50' : aiValidering[s.id].score >= 5 ? '#ff9800' : '#f44336'}`,
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        <div style={{fontWeight: 'bold', marginBottom: '5px'}}>
                          🤖 AI-vurdering: {aiValidering[s.id].score}/10
                        </div>
                        <div>{aiValidering[s.id].feedback}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* §2-22: Kostnadsanslag for TG3 (obligatorisk) */}
                {svar[s.id] && svar[s.id].includes('TG3') && (
                  <div style={{marginTop: '10px', padding: '15px', backgroundColor: '#ffebee', borderLeft: '3px solid #f44336'}}>
                    <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#c62828'}}>
                      💰 Kostnadsanslag (obligatorisk for TG3)
                    </p>
                    <p style={{margin: '0 0 12px 0', fontSize: '12px', color: '#666', fontStyle: 'italic'}}>
                      ℹ️ Bruk sjablongmessige intervaller basert på bransjenormer (Norsk Takst)
                    </p>

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Kostnadsintervall:
                    </label>
                    <select
                      value={kostnadsanslag[s.id]?.kategori || ''}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].kategori = e.target.value;
                        if (e.target.value !== 'eksakt') ny[s.id].eksakt = '';
                        setKostnadsanslag(ny);
                      }}
                      style={{width: '100%', padding: '8px', border: '1px solid #f44336', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px', marginBottom: '10px'}}
                    >
                      <option value="">-- Velg intervall --</option>
                      <option value="under_10k">Under 10 000 kr</option>
                      <option value="10k_50k">10 000 – 50 000 kr</option>
                      <option value="50k_100k">50 000 – 100 000 kr</option>
                      <option value="100k_300k">100 000 – 300 000 kr</option>
                      <option value="over_300k">Over 300 000 kr</option>
                      <option value="eksakt">Eksakt beløp (ved konkret anbud)</option>
                    </select>

                    {kostnadsanslag[s.id]?.kategori === 'eksakt' && (
                      <div style={{marginBottom: '10px'}}>
                        <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                          Eksakt beløp (NOK):
                        </label>
                        <input
                          type="number"
                          value={kostnadsanslag[s.id]?.eksakt || ''}
                          onChange={(e) => {
                            const ny = {...kostnadsanslag};
                            if (!ny[s.id]) ny[s.id] = {};
                            ny[s.id].eksakt = e.target.value;
                            setKostnadsanslag(ny);
                          }}
                          placeholder="Eksakt kostnad i NOK"
                          style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px'}}
                        />
                      </div>
                    )}

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Hva omfatter estimatet:
                    </label>
                    <textarea
                      value={kostnadsanslag[s.id]?.beskrivelse || ''}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].beskrivelse = e.target.value;
                        setKostnadsanslag(ny);
                      }}
                      placeholder="F.eks. 'Utbedring inkluderer materialer, arbeidskostnader og riving av gammel konstruksjon'"
                      rows="2"
                      style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '13px', marginBottom: '10px'}}
                    />

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Usikkerhetsnivå:
                    </label>
                    <select
                      value={kostnadsanslag[s.id]?.usikkerhet || 'middels'}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].usikkerhet = e.target.value;
                        setKostnadsanslag(ny);
                      }}
                      style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px', marginBottom: '10px'}}
                    >
                      <option value="lav">Lav - Basert på konkret anbud/erfaring</option>
                      <option value="middels">Middels - Basert på erfaringstall</option>
                      <option value="høy">Høy - Grovt anslag, krever nærmere undersøkelse</option>
                    </select>

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Anbefaling (valgfritt):
                    </label>
                    <textarea
                      value={kostnadsanslag[s.id]?.anbefaling || ''}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].anbefaling = e.target.value;
                        setKostnadsanslag(ny);
                      }}
                      placeholder="F.eks. 'Anbefales å innhente konkrete anbud fra minst 3 autoriserte entreprenører'"
                      rows="2"
                      style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '13px'}}
                    />

                    {(!kostnadsanslag[s.id]?.kategori) && (
                      <p style={{margin: '10px 0 0 0', fontSize: '12px', color: '#b71c1c'}}>
                        ⚠️ Kostnadsintervall må velges
                      </p>
                    )}
                  </div>
                )}

                {/* BILDESEKSJON: Spørsmål-spesifikke bilder for ALLE besvarte spørsmål */}
                {svar[s.id] && !svar[s.id].includes('IU') && (
                  <div className="sporsmal-bilde-seksjon" style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: (svar[s.id] === 'TG2' || svar[s.id] === 'TG3') ? '#fff3e0' : '#f0f8ff',
                    borderRadius: '6px',
                    border: (svar[s.id] === 'TG2' || svar[s.id] === 'TG3') ? '2px solid #ff9800' : '2px solid #2196f3'
                  }}>
                    <div style={{marginBottom: '12px'}}>
                      <h5 style={{margin: '0 0 5px 0', fontSize: '15px', color: '#333'}}>
                        📸 {(svar[s.id] === 'TG2' || svar[s.id] === 'TG3') ? 'Dokumenter mangel/skade' : 'Legg til bilder'}
                      </h5>
                      <p style={{margin: '0', color: '#666', fontSize: '12px', fontStyle: 'italic'}}>
                        Knyttet til: "{s.sporsmal_tekst}"
                      </p>
                      <p style={{color: '#666', fontSize: '13px', marginTop: '8px', marginBottom: '0'}}>
                        {(svar[s.id] === 'TG2' || svar[s.id] === 'TG3')
                          ? 'Last opp bilder som dokumenterer skaden/mangelen (obligatorisk for TG2/TG3)'
                          : 'Valgfritt: Last opp bilder som viser aktuelle forhold'
                        }
                      </p>
                    </div>

                    <BildeOpplaster
                      type="bygningsdel"
                      id={id}
                      sporsmalId={s.id}
                      bildeposisjon="sporsmal"
                      placeholder={`Ta bilde - ${s.sporsmal_tekst}`}
                      onUploadSuccess={() => fetchSporsmalBilder(s.id)}
                    />

                    <BildeGalleri
                      bilder={sporsmalBilder[s.id] || []}
                      onRefresh={() => fetchSporsmalBilder(s.id)}
                    />
                  </div>
                )}

                {s.obligatorisk && (!svar[s.id] || svar[s.id].trim() === '') && (
                  <div className="ikke-besvart-container" style={{marginTop: '10px'}}>
                    {/* Vis gul varselboks kun før brukeren begynner å skrive */}
                    {(!ikkeBesvartBegrunnelse[s.id] || ikkeBesvartBegrunnelse[s.id].trim() === '') ? (
                      <div style={{padding: '10px', backgroundColor: '#fff3cd', borderLeft: '3px solid #ffc107', marginBottom: '10px'}}>
                        <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold'}}>
                          ⚠️ Dette spørsmålet er obligatorisk
                        </p>
                        <p style={{margin: '0', fontSize: '13px'}}>
                          Enten besvar spørsmålet ovenfor, eller forklar hvorfor det ikke kan besvares nedenfor.
                        </p>
                      </div>
                    ) : (
                      <div style={{marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px'}}>
                        <span style={{fontSize: '12px', color: '#666', fontStyle: 'italic'}}>
                          Obligatorisk begrunnelse (må ikke besvares)
                        </span>
                        <button
                          type="button"
                          onClick={() => {/* Info-dialog kan implementeres senere */}}
                          style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#666'}}
                          title="Klikk for mer informasjon om obligatoriske spørsmål"
                        >
                          ℹ️
                        </button>
                      </div>
                    )}

                    <textarea
                      value={ikkeBesvartBegrunnelse[s.id] || ''}
                      onChange={(e) => setIkkeBesvartBegrunnelse({
                        ...ikkeBesvartBegrunnelse, [s.id]: e.target.value
                      })}
                      placeholder="F.eks: Ikke tilgjengelig for inspeksjon, skjult av konstruksjon, etc. (minimum 10 tegn)"
                      style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '8px',
                        border: (!ikkeBesvartBegrunnelse[s.id] || ikkeBesvartBegrunnelse[s.id].trim() === '')
                          ? '2px solid #ffc107'  // Gul kant før skrivstart
                          : '1px solid #ddd',     // Normal kant når de skriver
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        fontSize: '14px'
                      }}
                    />
                    {ikkeBesvartBegrunnelse[s.id] && ikkeBesvartBegrunnelse[s.id].length < 10 && (
                      <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#856404'}}>
                        Begrunnelsen må være minst 10 tegn ({ikkeBesvartBegrunnelse[s.id].length}/10)
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Bildeopplasting seksjon */}
        <div className="bilder-seksjon" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '15px' }}>📷 Dokumentasjon med bilder</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Last opp bilder for å dokumentere tilstanden til denne bygningsdelen.
          </p>

          <div style={{ marginBottom: '20px' }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleBildeUpload}
              disabled={uploadingBilde}
              style={{ display: 'none' }}
              id="bilde-upload"
            />
            <label
              htmlFor="bilde-upload"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: uploadingBilde ? '#ccc' : '#2196F3',
                color: 'white',
                borderRadius: '4px',
                cursor: uploadingBilde ? 'not-allowed' : 'pointer',
                fontWeight: '500'
              }}
            >
              {uploadingBilde ? '⏳ Laster opp...' : '📸 Last opp bilde'}
            </label>
          </div>

          {bilder.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
              {bilder.map((bilde) => (
                <div
                  key={bilde.id}
                  style={{
                    position: 'relative',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <img
                    src={bilde.cloudflare_r2_url}
                    alt={bilde.filnavn}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{ padding: '10px' }}>
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0', wordBreak: 'break-word' }}>
                      {bilde.filnavn}
                    </p>
                    <button
                      onClick={() => handleSlettBilde(bilde.id)}
                      style={{
                        width: '100%',
                        padding: '6px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      🗑️ Slett
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
              Ingen bilder lastet opp ennå
            </p>
          )}
        </div>

        {/* Videre undersøkelser */}
        <VidereUndersokelser
          eiendomId={bygningsdel.eiendom_id}
          bygningsdelId={parseInt(id)}
          bygningsdelType={bygningsdel.bygningsdel_type}
          eksisterendeUndersokelser={videreUndersokelser}
          regelForslag={regelForslag}
          onUndersokelseOpprettet={() => fetchVidereUndersokelser()}
          onUndersokelseSlettet={() => fetchVidereUndersokelser()}
        />

        <div className="befaring-actions">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary btn-large"
          >
            {saving ? 'Lagrer...' : 'Lagre'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BygningsdelBefaring;
